// Расчёты A/B: размер выборки, MDE, метрики временных окон. Чистые функции.
// Сессия — К2 (reached, convMask, day); окно — [день с, день по] включительно.

export const ZA = { 10: 1.6449, 5: 1.96, 1: 2.5758 };
export const ZP = { 80: 0.8416, 90: 1.2816, 95: 1.6449 };

// Двусторонняя нормальная аппроксимация для двух долей, как в прототипе.
export function requiredN(p1, effect, alpha, power) {
  if (!(p1 > 0 && p1 < 1) || !(alpha in ZA) || !(power in ZP)) return null;
  const p2 = Math.min(0.999, p1 * (1 + effect / 100));
  if (!(p2 > p1)) return Infinity;
  const z = ZA[alpha] + ZP[power];
  return Math.ceil(z * z * (p1 * (1 - p1) + p2 * (1 - p2)) / ((p2 - p1) ** 2));
}

// Бисекция: какой относительный эффект ловится на N сессиях. null — никакой.
export function mde(p1, n, alpha, power) {
  if (!(p1 > 0 && p1 < 1) || !(n > 0) || !(alpha in ZA) || !(power in ZP)) return null;
  const need = p2 => { const z = ZA[alpha] + ZP[power]; return z * z * (p1 * (1 - p1) + p2 * (1 - p2)) / ((p2 - p1) ** 2); };
  if (need(0.999) > n) return null;
  let lo = p1 + 1e-9, hi = 0.999;
  for (let i = 0; i < 60; i++) { const mid = (lo + hi) / 2; if (need(mid) > n) lo = mid; else hi = mid; }
  return Math.round((hi / p1 - 1) * 1000) / 10;
}

// Значение метрики по сессиям окна. Все метрики идут по одной воронке сессий (reached, convMask),
// в прототипе форма и отчёт считали по разным источникам. block — индекс блока (0..nBlocks-1).
// Проценты с одним знаком, как в прототипе; medBlock — номер блока с 1 (нижняя медиана).
// Сессии без блоков (reached = -1) в блочные метрики не входят (как в решётке), конверсия — по всем.
export function windowMetric(sessions, win, metric, block, nBlocks) {
  const s = sessions.filter(x => x.day >= win[0] && x.day <= win[1] && (metric === 'conv' || x.reached >= 0));
  const n = s.length;
  if (!n) return { n: 0, k: 0, value: null };
  const pct = k => Math.round(k / n * 1000) / 10;
  const reachedAt = b => s.filter(x => x.reached >= b).length;
  let value = null, k = null;   // k — сколько «успешных» сессий (конверсия, дошли до блока), для подписей «k из n»
  if (metric === 'conv') { k = s.filter(x => x.convMask).length; value = pct(k); }
  else if (metric === 'reach' && Number.isInteger(block)) { k = reachedAt(block); value = pct(k); }
  else if (metric === 'medBlock') value = s.map(x => x.reached + 1).sort((a, b) => a - b)[(n - 1) >> 1];
  else if (metric === 'gain') { k = reachedAt(nBlocks - 1); value = Math.round(k / n * 1000 / nBlocks) / 10; } // средний прирост на блок, п.п.
  return { n, k, value };
}

// Число сессий по дням: лента дней в отчёте и поток «в день» для срока теста.
export function perDay(sessions) {
  const out = Object.create(null);
  sessions.forEach(s => { out[s.day] = (out[s.day] || 0) + 1; });
  return out;
}

// Поток в день, как в прототипе: сессий на весь размах их дат (последний − первый + 1),
// пустые дни внутри размаха входят. Иначе редкий столбец выглядел бы богаче, чем есть.
export function flowPerDay(sessions) {
  if (!sessions.length) return 0;
  let a = Infinity, b = -Infinity;
  sessions.forEach(s => { if (s.day < a) a = s.day; if (s.day > b) b = s.day; });
  return sessions.length / (b - a + 1);
}

// Окна «до» / «после»; окно, в котором не осталось дней, — null (отчёт показывает его пустым).
// Сохранённые окна завершённого теста не пересчитываются, но зажимаются днями данных: срез мог сдвинуться.
// «До» не заходит на «после». firstDay (необязательный) не даёт «до» уйти левее данных.
export function splitWindows(test, lastDay, firstDay = -Infinity) {
  const clamp = w => {
    if (!w) return null;                 // у сохранённых windows может не быть before/after
    const r = [Math.max(firstDay, w[0]), Math.min(lastDay, w[1])];
    return r[0] <= r[1] ? r : null;
  };
  const cut = (before, after) => {
    if (before && after) before = clamp([before[0], Math.min(before[1], after[0] - 1)]);
    return { before, after };
  };
  if (test.windows) return cut(clamp(test.windows.before), clamp(test.windows.after));
  const len = Math.max(1, test.days || 14);
  const after = clamp([lastDay - len + 1, lastDay]);
  if (!after) return { before: null, after: null };   // firstDay правее lastDay: данных нет
  const lenBefore = Math.max(1, test.kind === 'prepost' ? (test.before || len) : len);
  return cut(clamp([after[0] - lenBefore, after[0] - 1]), after);
}

// Период короче срока теста: «после» съело все дни и «до» не осталось. Тогда делим имеющиеся дни
// пополам (при нечётном числе лишний день уходит в «до»), иначе отчёт пуст и тест не завершить.
// Один день данных не делится — окна остаются как есть.
export function halveIfNoBefore(w) {
  if (w.before || !w.after || w.after[1] <= w.after[0]) return w;
  const [a, b] = w.after, h = Math.floor((b - a + 1) / 2);
  return { before: [a, b - h], after: [b - h + 1, b] };
}
